import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./ImportClients.css";
import { API_URL } from "../../utils/auth";
import Auth from '../../utils/auth';

const ImportClients = () => {
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const fileInputRef = useRef(null);

  const workspaceId = Auth.getWorkspaceId();
  const user = Auth.getUser();
  const userId = user?.id;

  const handleDownloadTemplate = () => {
    const csvUrl = "https://docs.google.com/spreadsheets/d/18Cr5OfUt3we3YZWISD8-yvGXqGviqhJ9YqPCxIN6t88/edit?usp=sharing";
    const a = document.createElement("a");
    a.href = csvUrl;
    a.target = "_blank";
    a.download = "client_import_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setImportStatus(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.type === "text/csv" || droppedFile.name.endsWith('.csv'))) {
      setFile(droppedFile);
      setImportStatus(null);
    } else {
      setImportStatus({ success: false, message: "Please upload only CSV files." });
    }
  };

  const handleVideoModal = (e) => {
    e.preventDefault();
    setShowVideoModal(true);
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
  };

  // Load Vidalytics script when modal opens
  useEffect(() => {
    if (showVideoModal) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.innerHTML = `
        (function (v, i, d, a, l, y, t, c, s) {
          y='_'+d.toLowerCase();c=d+'L';if(!v[d]){v[d]={};}if(!v[c]){v[c]={};}if(!v[y]){v[y]={};}var vl='Loader',vli=v[y][vl],vsl=v[c][vl + 'Script'],vlf=v[c][vl + 'Loaded'],ve='Embed';
          if (!vsl){vsl=function(u,cb){
            if(t){cb();return;}s=i.createElement("script");s.type="text/javascript";s.async=1;s.src=u;
            if(s.readyState){s.onreadystatechange=function(){if(s.readyState==="loaded"||s.readyState=="complete"){s.onreadystatechange=null;vlf=1;cb();}};}else{s.onload=function(){vlf=1;cb();};}
            i.getElementsByTagName("head")[0].appendChild(s);
          };};
          vsl(l+'loader.min.js',function(){if(!vli){var vlc=v[c][vl];vli=new vlc();}vli.loadScript(l+'player.min.js',function(){var vec=v[d][ve];t=new vec();t.run(a);});});
        })(window, document, 'Vidalytics', 'vidalytics_embed_LkeBO9Pcs5pAsohE', 'https://fast.vidalytics.com/embeds/56hJ9E4N/LkeBO9Pcs5pAsohE/');
      `;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [showVideoModal]);

  const handleImport = async () => {
    if (!file) {
      setImportStatus({ success: false, message: "Please select a file first." });
      return;
    }

    if (!workspaceId || !userId) {
      setImportStatus({
        success: false,
        message: "Workspace or user information not available. Please log in again."
      });
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("workspaceId", workspaceId);
      formData.append("userId", userId);

      const response = await axios.post(
        `${API_URL}/utils/import-clients`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          params: {
            workspaceId,
            userId
          }
        }
      );

      if (response.data) {
        if (response.data.error) {
          setImportStatus({
            success: false,
            message: response.data.message || response.data.error,
            details: {}
          });
          return;
        }

        const parseImportResponse = (data) => {
          // New format: { total, queued, errors }
          if (typeof data.total !== 'undefined' || typeof data.queued !== 'undefined') {
            return {
              total: data.total ?? 0,
              successful: data.queued ?? 0,
              errors: data.errors || [],
              format: 'new'
            };
          }
          
          // Check for legacy format: { imported, failed, details: { errors } }
          if (typeof data.imported !== 'undefined') {
            return {
              total: data.imported ?? 0,
              successful: data.imported ?? 0,
              errors: data.details?.errors || [],
              format: 'legacy'
            };
          }
          
          // Fallback
          return {
            total: 0,
            successful: 0,
            errors: data.errors || [],
            format: 'unknown'
          };
        };

        const { total, successful, errors } = parseImportResponse(response.data);

        if (errors.length > 0) {
          const failureMessage = successful > 0 
            ? `Queued ${successful} client${successful !== 1 ? 's' : ''} for import! ${errors.length} duplicate email${errors.length !== 1 ? 's' : ''} found:`
            : `No clients imported. ${errors.length} duplicate email${errors.length !== 1 ? 's' : ''} found:`;
          
          setImportStatus({
            success: successful > 0,
            total,
            successful,
            failed: errors.length,
            message: failureMessage,
            details: { errors }
          });
        } else if (total > 0) {
          setImportStatus({
            success: true,
            total,
            successful: total,
            failed: 0,
            message: `Successfully queued ${total} client${total !== 1 ? 's' : ''} for import!`,
            details: {}
          });
        } else {
          setImportStatus({
            success: false,
            message: response.data.message || "No clients were queued for import.",
            details: {}
          });
        }
      }
    } catch (error) {
      console.error("Import error:", error);
      const errorMessage = error.response?.data?.message ||
        error.message ||
        "Failed to import clients. Please try again.";
      setImportStatus({ success: false, message: errorMessage });
    } finally {
      setIsLoading(false);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="import-clients">
      <h2>Import Clients</h2>
      <p className="description">
        Upload a CSV file with customer information. Make sure the file is formatted correctly.
      </p>

      <div className="template-section">
        <button onClick={handleDownloadTemplate} className="download-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          Download Template
        </button>
        <span className="hint">(Includes 5 examples of clients with format)</span>
      </div>


      <div className="demo-link">
        <a href="#" className="demo-link-text" onClick={handleVideoModal}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <circle cx="12" cy="12" r="10"></circle>
            <polygon points="10 8 16 12 10 16 10 8"></polygon>
          </svg>
          View a quick demo on uploading clients
        </a>
      </div>

      <div 
        className={`drop-zone ${isDragging ? "dragging" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input 
          type="file" 
          id="file-upload" 
          accept=".csv" 
          onChange={handleFileChange}
          ref={fileInputRef}
          style={{ display: "none" }}
        />
        <label htmlFor="file-upload" className="drop-zone-label">
          {file ? (
            <div className="file-info">
              <span className="file-name">{file.name}</span>
              <span className="file-size">{(file.size / 1024).toFixed(2)} KB</span>
              <span className="file-type">{file.name.split('.').pop().toUpperCase()}</span>
            </div>
          ) : (
            <>
              <div className="upload-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#614BEA" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
              </div>
              <p>Drag your file here or click to select</p>
              <p className="hint">Accepted formats: CSV (max. 5MB)</p>
            </>
          )}
        </label>
      </div>

      {file && (
        <div className="actions">
          <button onClick={() => { 
            setFile(null); 
            setImportStatus(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          }} className="cancel-btn">
            Cancel
          </button>
          <button 
            onClick={handleImport} 
            className="import-btn"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Importing...
              </>
            ) : (
              "Import Clients"
            )}
          </button>
        </div>
      )}

      {importStatus && (
        <div className={`import-status ${importStatus.details?.errors?.length > 0 ? "error" : "success"}`}>
          <div className="import-summary">
            <div className="summary-stats">
              <div className="stat total">
                <span className="stat-label">Total</span>
                <span className="stat-value">{importStatus.total}</span>
              </div>
              <div className="stat success">
                <span className="stat-label">Queued</span>
                <span className="stat-value">{importStatus.successful}</span>
              </div>
              <div className="stat failed">
                <span className="stat-label">Error</span>
                <span className="stat-value">{importStatus.failed}</span>
              </div>
            </div>

            <p className="import-message">{importStatus.message}</p>
            
            {importStatus.details?.errors?.length > 0 && (
              <div className="error-details">
                <div className="error-list-container">
                  {(() => {
                    // Group errors by type
                    const groupedErrors = {};
                    importStatus.details.errors.forEach((error) => {
                      const errorType = error.error || error.message || 'Unknown error';
                      if (!groupedErrors[errorType]) {
                        groupedErrors[errorType] = [];
                      }
                      groupedErrors[errorType].push(error);
                    });

                    return (
                      <div className="grouped-errors">
                        {Object.entries(groupedErrors).map(([errorType, errors]) => (
                          <div key={errorType} className="error-group">
                            <div className="error-type">{errorType}</div>
                            <div className="error-rows">
                              Rows: {errors.map(e => e.rowNum).join(', ')}
                            </div>
                            {errors.some(e => e.firstName || e.lastName || e.email) && (
                              <ul className="error-list">
                                {errors.map((error, index) => {
                                  const firstName = error.firstName || '';
                                  const lastName = error.lastName || '';
                                  const email = error.email || '';
                                  const fullName = `${firstName} ${lastName}`.trim();
                                  
                                  return (
                                    <li key={index} className="error-item">
                                      {fullName && <div className="error-name">{fullName}</div>}
                                      {email && <div className="error-email">{email}</div>}
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && (
        <div className="video-modal-overlay" onClick={closeVideoModal}>
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="video-modal-close" onClick={closeVideoModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <div className="video-container">
              <div id="vidalytics_embed_LkeBO9Pcs5pAsohE" style={{width: '100%', position: 'relative', paddingTop: '56.25%'}}></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportClients;